<template>
  <div class="round-results">
    <!-- Loop through each round result and display it as a tooltip -->
    <span v-for="(roundResult, i) in roundResults" :key="i">
      <b-tooltip
        v-if="roundResult"
        :type="roundResult.style"
        position="is-bottom"
      >
        <!-- Link to the game if it has a match -->
        <router-link :to="roundResult.hasMatch ? roundResult.to : ''">
          <span
            class="result-symbol tag"
            :class="`${roundResult.roundStyle} ${roundResult.textStyle}`"
          >
            {{ roundResult.symbol }}
          </span>
        </router-link>

        <!-- Tooltip content showing detailed result information -->
        <template v-slot:content>
          <p>{{ getResultText(roundResult.game, i + 1) }}</p>
        </template>
      </b-tooltip>
    </span>
  </div>
</template>

<script>
import GameHelper from "@/utils/game-helper";
import gameUrls from "@/mixins/gameUrls";

// Constants for symbols
const SYMBOLS = {
  BLACK_PIECE: "♞", // Black knight symbol for black pieces
  WHITE_PIECE: "♞", // Black knight symbol for white pieces (using same for consistency)
  BYE: "ദ", // Symbol for bye rounds
  NOT_PAIRED: "▯", // Symbol for participated but not paired
  WILL_PARTICIPATE: "✔", // Symbol for will participate in future rounds
  NOT_PARTICIPATING: "✗", // Symbol for not participating
};

// Constants for style classes
const STYLES = {
  FUTURE_ROUND: "is-light",
  NO_GAME: "is-dark",
  WIN: "is-success",
  DRAW: "is-warning",
  LOSS: "is-danger",
  DEFAULT: "is-info",
};

// Constants for text styles
const TEXT_STYLES = {
  BLACK: "has-text-dark",
  WHITE: "has-text-white",
  FUTURE: "has-text-grey",
  DEFAULT: "",
};

/**
 * Component to display round results for a player in a tournament
 */
export default {
  name: "RoundResults",

  mixins: [gameUrls],

  props: {
    /**
     * Bit mask representing rounds the player is participating in
     * Each bit position (0-indexed) represents a round number (1-indexed)
     */
    roundsParticipated: {
      type: Number,
      required: true,
    },

    /**
     * The current round number of the tournament
     * When currentRoundNumber is not defined, it will toggle participation for all rounds
     */
    currentRoundNumber: {
      type: Number,
      required: false,
    },

    /**
     * Total number of rounds in the tournament
     */
    totalRounds: {
      type: Number,
      required: true,
    },

    /**
     * Username of the player whose results we're displaying
     */
    username: {
      type: String,
      required: true,
    },

    /**
     * Array of game objects for the player
     */
    games: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    /**
     * Generates an array of round result objects for display
     * @returns {Array} Array of round result objects
     */
    roundResults() {
      const results = new Array(this.totalRounds);

      // Process each round up to the current round number
      for (let i = 0; i < this.totalRounds; i++) {
        const roundNumber = i + 1;

        // Find the game for this round if it exists
        const game = this.games
          ? this.games.find((g) => g.roundNumber === roundNumber)
          : null;

        // Only process rounds up to the current round number
        if (roundNumber > this.currentRoundNumber) {
          results[i] = this.createFutureRoundResult(roundNumber);
          continue;
        }

        // Create the round result object
        results[i] = {
          roundNumber,
          symbol: this.getRoundSymbol(game, roundNumber),
          style: this.getRoundStyle(game, roundNumber) || STYLES.DEFAULT,
          roundStyle: this.getRoundStyle(game, roundNumber),
          textStyle: this.getTextStyle(game, roundNumber),
          playerColor: this.getPlayerColor(game),
          hasMatch: this.hasMatch(game),
          to: this.hasMatch(game) ? this.getGameUrl(game.matchGUID) : null,
          game,
        };
      }
      return results;
    },
  },

  methods: {
    /**
     * Creates a result object for future rounds
     * @param {Number} roundNumber - The round number
     * @returns {Object} Future round result object
     */
    createFutureRoundResult(roundNumber) {
      const isParticipating = this.isParticipatingInRound(roundNumber);

      return {
        roundNumber,
        symbol: isParticipating
          ? SYMBOLS.WILL_PARTICIPATE
          : SYMBOLS.NOT_PARTICIPATING,
        style: STYLES.FUTURE_ROUND,
        roundStyle: STYLES.FUTURE_ROUND,
        textStyle: TEXT_STYLES.FUTURE,
        playerColor: null,
        hasMatch: false,
        to: null,
        game: null,
      };
    },

    /**
     * Determines if the player is participating in a specific round
     * @param {Number} roundNumber - The round number to check
     * @returns {Boolean} True if participating, false otherwise
     */
    isParticipatingInRound(roundNumber) {
      const mask = 1 << (roundNumber - 1);
      return (this.roundsParticipated & mask) !== 0;
    },

    /**
     * Gets the symbol to display for a round
     * @param {Object|null} game - The game object for the round
     * @param {Number} roundNumber - The round number
     * @returns {String} Symbol to display
     */
    getRoundSymbol(game, roundNumber) {
      // If there's a game, determine the symbol based on player's color
      if (game) {
        if (game.blackUsername === this.username) return SYMBOLS.BLACK_PIECE;
        if (game.blackUsername) return SYMBOLS.WHITE_PIECE;
        return SYMBOLS.BYE; // Bye round
      }

      // No game, check if participating
      if (this.isParticipatingInRound(roundNumber)) {
        // Participating but not paired yet
        return SYMBOLS.NOT_PAIRED;
      }

      // Not participating
      return SYMBOLS.NOT_PARTICIPATING;
    },

    /**
     * Gets the style class for a round
     * @param {Object|null} game - The game object for the round
     * @param {Number} roundNumber - The round number
     * @returns {String} Style class
     */
    getRoundStyle(game, roundNumber) {
      // Future rounds are light
      if (roundNumber > this.currentRoundNumber) return STYLES.FUTURE_ROUND;

      // No game means dark style
      if (!game) return STYLES.NO_GAME;

      // Get the result for the player
      const resultEnum = this.getPlayerResult(game);
      if (resultEnum == null) return null;

      // Determine style based on score
      const score = GameHelper.tournamentResultScore(resultEnum);
      if (score === 1) return STYLES.WIN;
      if (score === 0.5) return STYLES.DRAW;
      if (score === 0) return STYLES.LOSS;

      return STYLES.DEFAULT;
    },

    /**
     * Gets the player's result from a game
     * @param {Object} game - The game object
     * @returns {Number|null} The result enum value or null
     */
    getPlayerResult(game) {
      if (!game) return null;
      return game.whiteUsername === this.username
        ? game.whiteResult
        : game.blackResult;
    },

    /**
     * Gets the player's color in a game
     * @param {Object|null} game - The game object
     * @returns {String|null} 'black', 'white', or null
     */
    getPlayerColor(game) {
      if (!game) return null;
      if (game.blackUsername === this.username) return "black";
      if (game.blackUsername) return "white";
      return null;
    },

    /**
     * Gets the text style for a round
     * @param {Object|null} game - The game object for the round
     * @param {Number} roundNumber - The round number
     * @returns {String} Text style class
     */
    getTextStyle(game, roundNumber) {
      const playerColor = this.getPlayerColor(game);
      if (playerColor === "black") return TEXT_STYLES.BLACK;
      if (playerColor === "white") return TEXT_STYLES.WHITE;
      if (roundNumber > this.currentRoundNumber) return TEXT_STYLES.FUTURE;
      return TEXT_STYLES.DEFAULT;
    },

    /**
     * Gets the text to display in the tooltip for a round
     * @param {Object|null} game - The game object for the round
     * @param {Number} roundNumber - The round number
     * @returns {String} Text to display
     */
    getResultText(game, roundNumber) {
      // Future rounds
      if (roundNumber > this.currentRoundNumber) {
        return `Round ${roundNumber} hasn't started yet`;
      }

      // No game
      if (!game) {
        if (this.isParticipatingInRound(roundNumber)) return "Not participated";
        return "Not participated";
      }

      // Player is white
      if (game.whiteUsername === this.username) {
        let resultText = GameHelper.tournamentResultText(game.whiteResult);
        if (game.blackUsername) resultText += ` vs. ${game.blackUsername}`;
        return resultText;
      }

      // Player is black
      if (game.blackUsername === this.username) {
        let resultText = GameHelper.tournamentResultText(game.blackResult);
        if (game.whiteUsername) resultText += ` vs. ${game.whiteUsername}`;
        return resultText;
      }

      return "-";
    },

    /**
     * Checks if a game has a match
     * @param {Object|null} game - The game object
     * @returns {Boolean} True if the game has a match, false otherwise
     */
    hasMatch(game) {
      return game && game.matchGUID && game.blackUsername;
    },

    /**
     * Formats a score for display
     * @param {Number} resultEnum - The result enum value
     * @returns {String} Formatted score
     */
    getScoreText(resultEnum) {
      const score = GameHelper.tournamentResultScore(resultEnum);
      return score === 0.5 ? "½" : score.toString();
    },
  },
};
</script>

<style scoped>
.round-results {
  display: inline-flex;
  flex-wrap: wrap;
}

.result-symbol {
  font-size: 1.5rem;
  font-family: monospace;
  line-height: 1;
}

.tag {
  padding: 0 5px;
  margin: 0 1px;
  transition: transform 0.1s ease-in-out;
}

.tag:hover {
  transform: translateY(-2px);
}
</style>
